import { useMemo } from 'react'
import SettingsSection from '../components/Settings/SettingsSection'
import useSlaSettings from '../hooks/useSlaSettings'
import useTicketPriorities from '../hooks/useTicketPriorities'
import { TICKET_TYPE_TREE } from '../utils/ticketType'
import { PRIORITY_META, getPriorityMeta } from '../utils/priorityMatrix'
import '../styles/data-table.css'

/** Texto corto del origen de una sugerencia. */
const SOURCE_LABEL = {
  cell: (n) => `${n} tickets`,
  priority: (n) => `por prioridad (${n})`,
  current: () => 'sin datos: se mantiene',
}

/**
 * Configuración → Tiempos de SLA: tiempo objetivo de primera respuesta y de
 * resolución por tipo de registro y prioridad. Se editan a mano (en horas) o
 * se autocalculan con el histórico de tickets (percentil 80).
 *
 * @component
 * @returns {JSX.Element}
 */
const SlaSettings = () => {
  const { priorities } = useTicketPriorities()
  // Columnas: prioridades del catálogo (o las conocidas si aún no cargan).
  const columns = useMemo(() => {
    const list = priorities.length
      ? [...priorities].sort((a, b) => a.id - b.id)
      : Object.entries(PRIORITY_META).map(([id, m]) => ({
          id: Number(id),
          name: m.label,
        }))
    return list.map((p) => ({ ...p, color: getPriorityMeta(p.id).color }))
  }, [priorities])
  const priorityIds = useMemo(() => columns.map((c) => c.id), [columns])

  const sla = useSlaSettings(priorityIds)
  const hasErrors = Object.keys(sla.errors).length > 0

  return (
    <SettingsSection
      title="Tiempos de SLA"
      icon="fa-stopwatch"
      description="Tiempo objetivo de primera respuesta y de resolución por tipo y prioridad. Se aplica a los tickets que se creen desde que se guarda."
      actions={
        <>
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            onClick={sla.autoCalculate}
            disabled={sla.loading || sla.calculating}
            title="Percentil 80 de los tiempos reales de los tickets existentes"
          >
            {sla.calculating ? (
              <span className="spinner-border spinner-border-sm me-2" />
            ) : (
              <i className="fa-solid fa-wand-magic-sparkles me-2" />
            )}
            Autocalcular
          </button>
          <button
            type="button"
            className="btn btn-sm btn-light"
            onClick={sla.resetToDefaults}
            disabled={sla.loading}
          >
            Valores por defecto
          </button>
        </>
      }
    >
      {sla.loading || !sla.draft ? (
        <div className="sla-empty">
          <span className="spinner-border spinner-border-sm text-primary" />
        </div>
      ) : (
        <>
          <p className="sla-help">
            Tiempos en <strong>horas</strong> (se aceptan decimales: 0.5 = 30
            min). Deje una celda vacía para que esa prioridad no lleve SLA.
            {sla.suggestions &&
              ' Debajo de cada celda se indica en qué se basa la sugerencia.'}
          </p>

          <div className="tt" style={{ flex: 'none' }}>
            <div className="tt-scroll">
              <table className="tt-table sla-table">
                <thead>
                  <tr>
                    <th>Tipo de registro</th>
                    {columns.map((c) => (
                      <th key={c.id}>
                        <span className="sla-prio">
                          <span
                            className="tt-dot"
                            style={{ background: c.color }}
                          />
                          {c.name}
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {TICKET_TYPE_TREE.map((group) => [
                    <tr key={`g-${group.value}`} className="sla-group">
                      <td colSpan={columns.length + 1}>{group.label}</td>
                    </tr>,
                    ...group.types.map((type) => {
                      const row = sla.draft[type.value]
                      return (
                        <tr
                          key={type.value}
                          className={row.enabled ? '' : 'is-off'}
                        >
                          <td>
                            <label className="sla-type">
                              <span className="form-check form-switch m-0">
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  checked={row.enabled}
                                  onChange={() => sla.toggleType(type.value)}
                                  aria-label={`${type.label} lleva SLA`}
                                />
                              </span>
                              <i
                                className={`fa-solid ${type.icon}`}
                                style={{ color: type.color }}
                              />
                              {type.label}
                            </label>
                            {!row.enabled && (
                              <span className="sla-off">Sin SLA</span>
                            )}
                          </td>
                          {columns.map((c) => {
                            const cell = row.cells[c.id]
                            const error = sla.errors[`${type.value}:${c.id}`]
                            const hint = sla.suggestions?.[type.value]?.[c.id]
                            return (
                              <td key={c.id}>
                                <div
                                  className={`sla-cell ${error ? 'is-invalid' : ''}`}
                                  title={error}
                                >
                                  <label>
                                    <span>Resp.</span>
                                    <input
                                      type="text"
                                      inputMode="decimal"
                                      value={cell.response}
                                      disabled={!row.enabled}
                                      onChange={(e) =>
                                        sla.setCell(
                                          type.value,
                                          c.id,
                                          'response',
                                          e.target.value
                                        )
                                      }
                                      aria-label={`${type.label}, ${c.name}: primera respuesta en horas`}
                                    />
                                    <span>h</span>
                                  </label>
                                  <label>
                                    <span>Resol.</span>
                                    <input
                                      type="text"
                                      inputMode="decimal"
                                      value={cell.resolution}
                                      disabled={!row.enabled}
                                      onChange={(e) =>
                                        sla.setCell(
                                          type.value,
                                          c.id,
                                          'resolution',
                                          e.target.value
                                        )
                                      }
                                      aria-label={`${type.label}, ${c.name}: resolución en horas`}
                                    />
                                    <span>h</span>
                                  </label>
                                </div>
                                {row.enabled && hint && (
                                  <span
                                    className={`sla-hint is-${hint.source}`}
                                  >
                                    {SOURCE_LABEL[hint.source](hint.samples)}
                                  </span>
                                )}
                              </td>
                            )
                          })}
                        </tr>
                      )
                    }),
                  ])}
                </tbody>
              </table>
            </div>
          </div>

          <div className="sla-footer">
            <span className="sla-meta">
              {hasErrors ? (
                <span className="text-danger">
                  <i className="fa-solid fa-circle-exclamation me-1" />
                  Corrija las celdas marcadas en rojo.
                </span>
              ) : sla.meta ? (
                `Última actualización: ${new Date(sla.meta.at).toLocaleString('es-CR')}${sla.meta.by ? ` · ${sla.meta.by}` : ''}`
              ) : (
                'Usando los valores por defecto del sistema.'
              )}
            </span>
            <div className="d-flex gap-2">
              <button
                type="button"
                className="btn btn-sm btn-light"
                onClick={sla.discard}
                disabled={!sla.dirty || sla.saving}
              >
                Descartar
              </button>
              <button
                type="button"
                className="btn btn-sm btn-primary"
                onClick={sla.save}
                disabled={!sla.dirty || hasErrors || sla.saving}
              >
                {sla.saving && (
                  <span className="spinner-border spinner-border-sm me-2" />
                )}
                Guardar
              </button>
            </div>
          </div>
        </>
      )}

      <style>{`
        .sla-empty { padding: 32px; text-align: center; }
        .sla-help { margin: 0; font-size: 0.78rem; color: var(--gm-text-muted); }
        .sla-table td { vertical-align: top; height: auto !important; padding-top: 8px !important; padding-bottom: 8px !important; }
        .sla-prio { display: inline-flex; align-items: center; gap: 6px; }
        .sla-group td {
          padding-top: 10px !important; padding-bottom: 4px !important; background: var(--gm-surface-2);
          font-size: 0.66rem; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase;
          color: var(--gm-text-muted) !important;
        }
        .sla-type { display: flex; align-items: center; gap: 8px; margin: 0; font-weight: 600; cursor: pointer; }
        .sla-type i { width: 14px; text-align: center; font-size: 0.75rem; }
        .sla-off { display: block; margin: 2px 0 0 42px; font-size: 0.7rem; color: var(--gm-text-faint); }
        tr.is-off .sla-cell { opacity: 0.4; }
        .sla-cell {
          display: flex; flex-direction: column; gap: 3px; padding: 4px 6px; border-radius: 8px;
          border: 1px solid transparent;
        }
        .sla-cell.is-invalid { border-color: var(--gm-danger); background: rgba(220, 53, 69, 0.08); }
        .sla-cell label { display: flex; align-items: center; gap: 4px; margin: 0; font-size: 0.68rem; color: var(--gm-text-muted); }
        .sla-cell label > span:first-child { width: 34px; }
        .sla-cell input {
          width: 58px; height: 24px; padding: 0 6px; border-radius: 5px; text-align: right;
          border: 1px solid var(--gm-border); background: var(--gm-surface); color: var(--gm-text);
          font-size: 0.78rem; font-variant-numeric: tabular-nums;
        }
        .sla-cell input:focus { outline: none; border-color: var(--gm-primary); }
        .sla-cell input:disabled { background: transparent; }
        .sla-hint { display: block; margin: 2px 0 0 6px; font-size: 0.65rem; }
        .sla-hint.is-cell { color: var(--gm-success); }
        .sla-hint.is-priority { color: var(--accent); }
        .sla-hint.is-current { color: var(--gm-text-faint); }
        .sla-footer { display: flex; flex-wrap: wrap; justify-content: space-between; align-items: center; gap: 10px; }
        .sla-meta { font-size: 0.75rem; color: var(--gm-text-muted); }
      `}</style>
    </SettingsSection>
  )
}

export default SlaSettings
